import PageHeader from "@/components/PageHeader";
import { useToast } from "@/components/ui/use-toast";
import { PATH_ROOT, PATH_SURVEYS } from "@/constants";
import { ApiError } from "@/services/api";
import { createSurvey } from "@/services/survey";
import { ReadSurvey, type WriteSurvey } from "@/types/survey";
import { useMutation } from "@tanstack/react-query";
import { exists } from "i18next";
import { NotepadText } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SurveyForm from "./SurveyForm";
import AddQuestionButton from "./components/Toolbar/components/AddQuestionButton";
import AddQuestionsFromTemplateButton from "./components/Toolbar/components/AddQuestionsFromTemplateButton";
import ClearButton from "./components/Toolbar/components/ClearButton";
import SaveButton from "./components/Toolbar/components/SaveButton";

const SurveyCreator = () => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const mutationFn = async (values: WriteSurvey) => {
    const response = await createSurvey(values);
    return {
      ...values,
      id: response.id,
    };
  };

  const surveyMutation = useMutation<ReadSurvey, ApiError, WriteSurvey>({
    mutationFn,
    onSuccess: ({ id }) => {
      navigate(`${PATH_SURVEYS}/${id}`);
      toast({
        title: t("surveyEditor.success.createSurvey"),
      });
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");
      toast({
        title: t("surveyEditor.errors.createSurvey"),
        description,
        variant: "destructive",
      });
    },
  });

  const breadcrumbItems = useMemo(
    () => [
      { label: t("breadcrumb.home"), href: PATH_ROOT },
      { label: t("breadcrumb.surveys"), href: PATH_SURVEYS },
      { label: t("breadcrumb.newSurvey") },
    ],
    [t],
  );

  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbItems} />
      <SurveyForm
        isNew
        onSubmit={surveyMutation.mutateAsync}
        busy={surveyMutation.isPending}
        icon={<NotepadText className="w-6 h-6 mr-3" />}
        title={t("surveyEditor.surveyTitle")}
        buttons={
          <>
            <SaveButton />
            <AddQuestionButton />
            <AddQuestionsFromTemplateButton />
            <ClearButton />
          </>
        }
        interQuestionToolbar={(questionIndex) => (
          <>
            <AddQuestionButton
              insertAt={questionIndex}
              label={t("surveyEditor.toolbar.insertQuestion")}
            />
            <AddQuestionsFromTemplateButton
              insertAt={questionIndex}
              label={t("surveyEditor.toolbar.insertQuestionsFromTemplate")}
            />
          </>
        )}
      />
    </>
  );
};

export default SurveyCreator;
